import { Breadcrumb, BreadcrumbIcon, BreadcrumbLink, BreadcrumbPage } from '@ads/components-react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/shared/ui/atoms';
import { PageHeader } from '@/shared/ui/organisms/PageHeader';
import type { QuestionDetail } from '../../../hooks/useQuestionEdit';
import { QuestionEditForm } from '../../organisms/QuestionEditForm';

// ─── Tipos ────────────────────────────────────────────────────────────────────

/** Props do template de edição de questão */
export interface EditQuestionTemplateProps {
  /** Dados completos da questão. Null durante o loading. */
  readonly question: QuestionDetail | null;
  /** Estado de carregamento */
  readonly isLoading: boolean;
  /** Estado de salvamento */
  readonly isSaving: boolean;
  /** Modo de operação: criação ou edição */
  readonly mode?: 'create' | 'edit';
  /** Callback ao salvar */
  readonly onSave: (data: Partial<QuestionDetail>) => void;
  /** Callback ao cancelar */
  readonly onCancel: () => void;
}

// ─── Sub-componente: conteúdo ────────────────────────────────────────────────

interface EditQuestionContentProps {
  readonly question: QuestionDetail;
  readonly isSaving: boolean;
  readonly mode: 'create' | 'edit';
  readonly onSave: (data: Partial<QuestionDetail>) => void;
  readonly onCancel: () => void;
}

function EditQuestionContent({
  question,
  isSaving,
  mode,
  onSave,
  onCancel,
}: EditQuestionContentProps) {
  const { t } = useTranslation('questionLibrary');

  return (
    <section className="overflow-hidden" aria-label={t('edit.settings.title')}>
      <QuestionEditForm
        question={question}
        mode={mode}
        onSave={onSave}
        onCancel={onCancel}
        isSaving={isSaving}
      />
    </section>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

/**
 * Template da página de edição de questão.
 *
 * Define a estrutura visual da página sem lógica de negócio:
 * - Breadcrumb: Home → Biblioteca de Questões → Editar Questão
 * - PageHeader com título da questão
 * - Painel de preview live sincronizado com o formulário
 * - Formulário de configurações com abas
 *
 * @example
 * ```tsx
 * <EditQuestionTemplate
 *   question={questionDetail}
 *   isLoading={false}
 *   isSaving={false}
 *   onSave={save}
 *   onCancel={() => navigate({ to: '/question-library' })}
 * />
 * ```
 */
export function EditQuestionTemplate({
  question,
  isLoading,
  isSaving,
  mode = 'edit',
  onSave,
  onCancel,
}: EditQuestionTemplateProps) {
  const { t } = useTranslation('questionLibrary');

  const pageTitle = mode === 'create' ? t('new.title') : t('edit.title');

  return (
    <div className="flex flex-col gap-sm">
      {/* Header */}
      <PageHeader
        title={pageTitle}
        breadcrumb={
          <Breadcrumb>
            <BreadcrumbLink>
              <Link to="/">
                <BreadcrumbIcon />
              </Link>
            </BreadcrumbLink>
            <BreadcrumbLink asChild>
              <Link to="/question-library">{t('title')}</Link>
            </BreadcrumbLink>
            <BreadcrumbPage>{pageTitle}</BreadcrumbPage>
          </Breadcrumb>
        }
        actions={undefined}
      />

      {/* Conteúdo */}
      {isLoading ? (
        <EditQuestionSkeleton />
      ) : (
        question && (
          <EditQuestionContent
            question={question}
            isSaving={isSaving}
            mode={mode}
            onSave={onSave}
            onCancel={onCancel}
          />
        )
      )}
    </div>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function EditQuestionSkeleton() {
  return (
    <div className="flex flex-col gap-xs">
      <div className="rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-mini shadow-level1">
        <Skeleton className="w-48 mb-xs h-xs" />
        <div className="flex flex-col gap-nano">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-xxs w-full" />
          ))}
        </div>
      </div>

      <div className="rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-mini shadow-level1">
        <Skeleton className="w-40 mb-sm h-xs" />
        <div className="mb-xs grid grid-cols-2 gap-xs">
          <Skeleton className="h-xxxl" />
          <Skeleton className="h-xxxl" />
        </div>
        <div className="grid grid-cols-3 gap-xs">
          <Skeleton className="h-sm" />
          <Skeleton className="h-sm" />
          <Skeleton className="h-sm" />
        </div>
      </div>
    </div>
  );
}
